import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useSidebar from '../../hooks/useSidebar';
import clsx from 'clsx';
import { Link } from '../Link/Link';
import { Collapse } from '@mui/material';
import { FloatingFocusManager, FloatingPortal, safePolygon, shift, useFloatingRootContext, useHover, useInteractions } from '@floating-ui/react';
import { useRole } from '@floating-ui/react';
import { offset } from '@floating-ui/react';
import { useClick, useDismiss } from '@floating-ui/react';
import { autoUpdate } from '@floating-ui/react';
import { useFloating } from '@floating-ui/react';
import { useBreakpoint } from '../../hooks/useBreakpoint/useBreakpoint';
import { ContactSupportOutlined, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { useSidebarStore } from '../../stores/SidebarStore';

type SidebarButtonProps = {
	to?: string;
	text: string;
	icon?: React.ReactNode;
	selected?: boolean;
	secondaryIcon?: React.ReactNode;
	expanded?: boolean;
	onExpandChange?: (expanded: boolean) => void;
	onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
	floatingMenu?: boolean;
	className?: string;
	children?: React.ReactNode;
	positionRef?: React.RefObject<HTMLDivElement>;
};

export default function SidebarButton({
	to,
	text,
	icon,
	selected,
	secondaryIcon,
	expanded,
	onExpandChange: setExpanded,
	onClick,
	floatingMenu,
	positionRef,
	className,
	children,
}: SidebarButtonProps) {
	const expandable = !!children && !floatingMenu;
	const [floating, setFloating] = useState(false);
	const ref = useRef(null);
	const lg = useBreakpoint('@sidebar-lg', ref);
	const { setMobileOpen } = useSidebarStore();
	const floatingEnabled = floatingMenu;

	const navRight = document.querySelector('nav')?.getBoundingClientRect().right;
	const selfRight = ref.current?.getBoundingClientRect().right;

	const { refs, floatingStyles, context } = useFloating({
		open: floating,
		onOpenChange: setFloating,
		placement: 'right-start',
		middleware: [
			offset({
				// Needs to always be 5px off the edge of the nav element.
				// Without this, the offset would be calculated based on the right edge of
				// the button, which would be pushed to the left when the scroll bar is
				// visible, causing the menu to overlap.
				mainAxis: navRight - selfRight + 5,
			}),
			shift({ padding: 5 }),
		],
		whileElementsMounted: autoUpdate,
	});

	const click = useClick(context, { enabled: floatingEnabled });
	const dismiss = useDismiss(context, { enabled: floatingEnabled });
	const role = useRole(context, { enabled: floatingEnabled });
	const hover = useHover(context, { handleClose: safePolygon(), enabled: floatingEnabled });

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role,
		hover,
	]);

	const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		if (expandable) {
			setExpanded(!expanded);
		} else if (!floatingEnabled) {
			// Close mobile sidebar on click
			setMobileOpen(false);
		}
		onClick?.(e);
	};

	return (
		<div ref={ref}>
			<Link
				ref={refs.setReference}
				unstyled
				href={to}
				className={clsx(
					'group flex w-full items-center justify-center border-x-[5px] border-r-transparent px-[15px] py-4 transition-colors duration-50 box-border @min-sidebar-lg:max-h-[56px] @min-sidebar-lg:justify-between',
					selected && !expanded ? 'border-rose-600 bg-component-navbar-bg-primary_pressedactive text-component-navbar-text-desktop-primary_active' : 'border-transparent bg-component-navbar-bg-primary text-component-navbar-text-desktop-primary',
					'hover:bg-component-navbar-bg-primary_hover hover:text-component-navbar-text-desktop-primary_active',
					className,
				)}
				onClick={handleClick}
				{...(!lg ? getReferenceProps() : {})}
			>
				<div className="flex flex-wrap items-center justify-center gap-[12px] [&_svg]:size-[24px]! @min-sidebar-lg:[&_svg]:size-[18px]! @min-sidebar-lg:justify-start @min-sidebar-lg:gap-3">
					{ icon }
					<span className="@max-sidebar-lg:text-center text-sm font-(--navbar-font-weight) tracking-[0.14px]">{ text }</span>
				</div>
				{secondaryIcon && (
					<div className="hidden @min-sidebar-lg:block">
						{ secondaryIcon }
					</div>
				)}
				{(expandable || floatingEnabled) && (
					<div className="hidden text-component-navbar-text-desktop-primary @min-sidebar-lg:block">
						{expandable && (expanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />)}
						{floatingEnabled && <KeyboardArrowRight />}
					</div>
				)}
			</Link>
			{children && (
				<>
					{!floatingEnabled && (
						<Collapse
							appear={false}
							in={expanded}
						>
							{children}
						</Collapse>
					)}
					{floating && floatingEnabled && (
						<FloatingPortal>
							{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
							<div
								ref={refs.setFloating}
								style={floatingStyles}
								{...getFloatingProps()}
								className="flex flex-col overflow-clip rounded-lg bg-component-navbar-bg-primary py-2 shadow-md **:justify-start!"
								onClick={(e) => setFloating(false)}
							>
								{children}
							</div>
						</FloatingPortal>
					)}
				</>
			)}
		</div>
	);
}
